// @ts-check

import BaseModel from "@kaspernj/api-maker/build/base-model.js"
import Invoice from "./invoice.js"
import InvoicePaymentBambora from "./invoice-payment-bambora.js"

const modelClassData = {
  "attributes": {
    "amount": {
      "column": null,
      "name": "amount",
      "selected_by_default": null,
      "translated": null
    },
    "id": {
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      },
      "name": "id",
      "selected_by_default": null,
      "translated": null
    }
  },
  "className": "InvoicePayment",
  "collectionKey": "invoice_payments",
  "collectionName": "invoice_payments",
  "i18nKey": "invoice_payment",
  "camelizedLower": "invoicePayment",
  "name": "InvoicePayment",
  "nameDasherized": "invoice-payment",
  "pluralName": "invoice_payments",
  "ransackable_associations": [
    {
      "className": "Invoice",
      "collectionName": "invoices",
      "foreignKey": "invoice_id",
      "name": "invoice",
      "macro": "belongs_to",
      "resource_name": "Invoice",
      "through": null
    },
    {
      "className": "InvoicePaymentBambora",
      "collectionName": "invoice_payment_bamboras",
      "foreignKey": "invoice_payment_bambora_id",
      "name": "invoice_payment_bambora",
      "macro": "belongs_to",
      "resource_name": "InvoicePaymentBambora",
      "through": null
    },
    {
      "className": "Log",
      "collectionName": "logs",
      "foreignKey": "trackable_id",
      "name": "logs",
      "macro": "has_many",
      "resource_name": "Log",
      "through": null
    },
    {
      "className": "Tagging",
      "collectionName": "taggings",
      "foreignKey": "resource_id",
      "name": "taggings",
      "macro": "has_many",
      "resource_name": "Tagging",
      "through": null
    },
    {
      "className": "Tag",
      "collectionName": "tags",
      "foreignKey": "tag_id",
      "name": "tags",
      "macro": "has_many",
      "resource_name": "Tag",
      "through": "taggings"
    },
    {
      "className": "Tenant",
      "collectionName": "tenants",
      "foreignKey": "tenant_id",
      "name": "tenant",
      "macro": "belongs_to",
      "resource_name": "Tenant",
      "through": null
    },
    {
      "className": "WorkerPlugins::WorkplaceLink",
      "collectionName": "workplace_links",
      "foreignKey": "resource_id",
      "name": "workplace_links",
      "macro": "has_many",
      "resource_name": "WorkplaceLink",
      "through": null
    }
  ],
  "ransackable_attributes": [
    {
      "name": "amount_cents",
      "column": {
        "default": 0,
        "name": "amount_cents",
        "null": false,
        "type": "integer"
      }
    },
    {
      "name": "amount_currency",
      "column": {
        "default": "DKK",
        "name": "amount_currency",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "created_at",
      "column": {
        "default": null,
        "name": "created_at",
        "null": false,
        "type": "datetime"
      }
    },
    {
      "name": "id",
      "column": {
        "default": null,
        "name": "id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "id_string",
      "column": null
    },
    {
      "name": "invoice_id",
      "column": {
        "default": null,
        "name": "invoice_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "invoice_payment_bambora_id",
      "column": {
        "default": null,
        "name": "invoice_payment_bambora_id",
        "null": true,
        "type": "uuid"
      }
    },
    {
      "name": "old_transaction_identifier",
      "column": {
        "default": null,
        "name": "old_transaction_identifier",
        "null": true,
        "type": "string"
      }
    },
    {
      "name": "payment_type",
      "column": {
        "default": null,
        "name": "payment_type",
        "null": false,
        "type": "string"
      }
    },
    {
      "name": "tenant_id",
      "column": {
        "default": null,
        "name": "tenant_id",
        "null": false,
        "type": "uuid"
      }
    },
    {
      "name": "updated_at",
      "column": {
        "default": null,
        "name": "updated_at",
        "null": false,
        "type": "datetime"
      }
    }
  ],
  "ransackable_scopes": [
    {
      "name": "in_current_workplace"
    },
    {
      "name": "soft_deleted_filter"
    },
    {
      "name": "sort_by_in_current_workplace_asc"
    },
    {
      "name": "sort_by_in_current_workplace_desc"
    }
  ],
  "relationships": [
    {
      "className": "Invoice",
      "collectionName": "invoices",
      "foreignKey": "invoice_id",
      "name": "invoice",
      "macro": "belongs_to",
      "resource_name": "Invoice",
      "through": null
    },
    {
      "className": "InvoicePaymentBambora",
      "collectionName": "invoice_payment_bamboras",
      "foreignKey": "invoice_payment_bambora_id",
      "name": "invoice_payment_bambora",
      "macro": "belongs_to",
      "resource_name": "InvoicePaymentBambora",
      "through": null
    }
  ],
  "paramKey": "invoice_payment",
  "primaryKey": "id"
}

/** Frontend model for InvoicePayment. */
export default class InvoicePayment extends BaseModel {
  /** @returns {typeof modelClassData} */
  static modelClassData() {
    return modelClassData
  }

  /** @returns {any} */
  amount() {
    return this.readAttributeUnderscore("amount")
  }

  /** @returns {boolean} */
  hasAmount() {
    const value = this.amount()

    return this._isPresent(value)
  }

  /** @returns {string} */
  id() {
    return this.readAttributeUnderscore("id")
  }

  /** @returns {boolean} */
  hasId() {
    const value = this.id()

    return this._isPresent(value)
  }

  /** @returns {import("./invoice.js").default | null} */
  invoice() {
    return this._readBelongsToReflection({modelClass: Invoice, reflectionName: "invoice"})
  }

  /** @returns {Promise<import("./invoice.js").default | null>} */
  loadInvoice() {
    if (!("invoiceId" in this)) throw new Error("Foreign key method wasn't defined: invoiceId")

    const id = this.invoiceId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "invoice",
        model: this,
        modelClass: Invoice
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./invoice.js").default | null>} */
  ensureInvoiceLoaded() {
    return this.ensureAssociationLoaded("invoice")
  }

  /** @returns {import("./invoice-payment-bambora.js").default | null} */
  invoicePaymentBambora() {
    return this._readBelongsToReflection({modelClass: InvoicePaymentBambora, reflectionName: "invoice_payment_bambora"})
  }

  /** @returns {Promise<import("./invoice-payment-bambora.js").default | null>} */
  loadInvoicePaymentBambora() {
    if (!("invoicePaymentBamboraId" in this)) throw new Error("Foreign key method wasn't defined: invoicePaymentBamboraId")

    const id = this.invoicePaymentBamboraId()
    const ransack = {}

    ransack["id_eq"] = id

    return this._loadBelongsToReflection(
      {
        reflectionName: "invoice_payment_bambora",
        model: this,
        modelClass: InvoicePaymentBambora
      },
      {ransack}
    )
  }

  /** @returns {Promise<import("./invoice-payment-bambora.js").default | null>} */
  ensureInvoicePaymentBamboraLoaded() {
    return this.ensureAssociationLoaded("invoice_payment_bambora")
  }
}
